import { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { createSelector } from "reselect";
import { DEFAULT_BOT_MESSAGE_DELAY_SECONDS } from "../../constants";
import { useBotTimeout } from "../../hooks/useBotTimeout";
import { useCanHandleBotStep } from "../../hooks/useCanHandleBotSteps";
import {
    changeEngagementPhase,
    setActiveBotStep,
    setActiveBotStepStatus,
    updateConversationLabels
} from "../../store/actions/genericActions";
import { AppState, BotStepStatus, EngagementPhase } from "../../store/definitions";
import { BotStepType, UpdateConversationLabelsBotStep } from "../../types/bots";

interface UpdateConversationLabelsStepProps {
    botStep: UpdateConversationLabelsBotStep;
}

const routeToQueueStepSelector = createSelector(
    (state: AppState) => state,
    (state) => ({
        conversation: state.chat.conversation,
        isEscalated: state.bot.isEscalated
    })
);

export const UpdateConversationLabelsStep: FC<UpdateConversationLabelsStepProps> = ({ botStep }) => {
    const dispatch = useDispatch();
    const { canHandleBotStep } = useCanHandleBotStep(botStep, {
        requiredStepType: BotStepType.UpdateConversationLabels
    });
    const { conversation, isEscalated } = useSelector(routeToQueueStepSelector);

    const handleTimeout = useBotTimeout({
        waitInSeconds: botStep.waitInSeconds ?? DEFAULT_BOT_MESSAGE_DELAY_SECONDS
    });

    useEffect(() => {
        const handleBotStep = async () => {
            if (!canHandleBotStep) {
                return;
            }

            // Mark step as being processed, and hide message input
            dispatch(setActiveBotStepStatus(BotStepStatus.Processing));

            // adds some delay to the bot step
            await handleTimeout.start();

            // Update the conversation labels
            if (botStep.updateConversationLabels) {
                dispatch(updateConversationLabels(botStep.updateConversationLabels, conversation?.sid));
            }

            if (botStep.nextStepId) {
                dispatch(setActiveBotStepStatus(BotStepStatus.Unhandled));
                dispatch(setActiveBotStep(botStep.nextStepId));
            } else if (isEscalated) {
                dispatch(changeEngagementPhase({ phase: EngagementPhase.MessagingCanvas }));
            }
        };
        handleBotStep();
    }, [dispatch, conversation, canHandleBotStep, botStep]);

    return <></>;
};
